import { FC, Fragment, useContext, useEffect, useState } from 'react';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Button from 'components/ui/Button/Button';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import { BookModel, PassengerModel, PaxHotelType } from 'ts/hotel/classes/BookModel';
import {
  passengerValidation,
  shortPassengerValidation,
  sexOptions,
  titlePassengerOptions,
} from 'constants/hotel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChild,
  faHotel,
  faPerson,
} from '@fortawesome/free-solid-svg-icons';
import { BookResponse } from 'ts/hotel/interfaces/BookResponse';
import { addYears, formatPrice } from 'utils/utils';
import { validate } from 'utils/validation';
import { useTranslation } from 'react-i18next';
import { getNationality } from 'utils/hotel';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import OrderTripCompany from 'components/shared/OrderTripCompany';
import PhoneInput from 'components/shared/ui/PhoneInput';

import { useLocation } from 'react-router-dom';
import { S3FieldOptions } from 'ts/hotel/interfaces/Generic';
import { faPaste } from '@fortawesome/free-regular-svg-icons';
import { MainContext } from 'contexts/MainContext';
import { b2b } from 'constants/generic';

interface BookFormProps {
  book: BookResponse;
  onPressContinue: (book: BookResponse) => void;
  onChangeCompanyId: (companyId: string) => void;
  onChangeTripId: (tripId: string) => void;
  tripId: string;
  companyId: string;
  form: BookModel;
  setForm: React.Dispatch<React.SetStateAction<BookModel>>;
  isSubmitted: boolean;
  setIsSubmitted: (isSubmitted: boolean) => void;
  isApartmentAccepted: boolean;
  docket: string;
  tourSubject: string;
}
const BookForm: FC<BookFormProps> = ({
  book,
  form,
  setForm,
  isSubmitted,
  setIsSubmitted,
  docket,
  tourSubject,
  ...props
}) => {
  const [t] = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [cxlAccepted, setCxlAccepted] = useState(false);
  const [priceChangeAccepted, setPriceChangeAccepted] = useState(false);
  const cxlExpired = new Date(book.roomClass.cxl.limitDate) < new Date() && new Date(book.roomClass.cxl.limitDate) > new Date(2000, 1, 1);
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const [showIsPasteName, setShowIsPasteName] = useState(false);
  const { pathname } = useLocation();
  const { user, bookConfig } = useContext(MainContext);

  useEffect(() => {
    setShowIsPasteName(!!sessionStorage.getItem('activeMyOrderNames'));
  }, [pathname]);

  useEffect(() => {
    if (user && user.isAuth && !form.passengers[0].firstName) {
      const nationality = getNationality(user);
      let code = '+972';
      if (nationality?.toUpperCase() === 'IT') {
        code = '+39';
      }
      setForm((prev) => {
        const firstPassenger = {
          ...form.passengers[0],
          email: user.email,
          phone: code + user.phone,
        };
        return { passengers: [firstPassenger, ...prev.passengers.slice(1)] };
      });
    }
  }, [user]); // eslint-disable-line

  const onChange = (passenger: PassengerModel) => {
    const newPassengers = [...form.passengers];
    const index = form.passengers.findIndex((item) => item.paxCode === passenger.paxCode);

    newPassengers[index] = passenger;

    setForm({ passengers: newPassengers });
  };

  const confirm = async () => {
    setIsSubmitted(true);

    if (isValid()) {
      const bookRequest = {
        ...book,
        docket,
        tourSubject,
        orderId: book.orderId,
        paxes: book.paxes.map((pax) => {
          const passenger = form.passengers.find((item) => item.paxCode === pax.paxCode)!;

          let firstName = passenger.firstName;
          let lastName = passenger.lastName;

          if (!passenger.isFirstGuest && !passenger.firstName) {
            const firstRoomGuest = form.passengers.find(
              (item) => item.isFirstGuest && passenger.roomIndex === item.roomIndex
            )!;
            firstName = firstRoomGuest.firstName;
          }

          if (!passenger.isFirstGuest && !passenger.lastName) {
            const firstRoomGuest = form.passengers.find(
              (item) => item.isFirstGuest && passenger.roomIndex === item.roomIndex
            )!;
            lastName = firstRoomGuest.lastName;
          }

          let gender = passenger.gender;
          if (b2b) {
            gender = 'M';
            if (passenger.gender === 'C') {
              gender = 'C';
            } else if (['mrs', 'miss', 'ms'].includes(passenger.title)) {
              gender = 'F';
            }
          }

          return {
            ...pax,
            firstName,
            lastName,
            title: passenger.title,
            gender,
            passportNumber: passenger.passportNumber,
            passportValidTill: passenger.passportValidTill
              ? dateFormat(passenger.passportValidTill, 'yyyy-MM-dd')
              : pax.passportValidTill,
            phone: passenger.phone || null,
            email: passenger.email || null,
            dob: passenger.dob ? dateFormat(passenger.dob, 'yyyy-MM-dd') : pax.dob,
          };
        }),
      };
      props.onPressContinue(bookRequest);
    }
  };

  const isValid = () => {
    let result = true;
    form.passengers.forEach((passenger, index) => {
      const validation =
        index === 0
          ? {
            ...passengerValidation,
            passportNumber: {
              required:
                ['1', '2'].includes(bookConfig.passportNumber.mandatory) &&
                ['1', '2'].includes(bookConfig.passportNumber.display),
            },
            passportValidTill: {
              required:
                ['1', '2'].includes(bookConfig.passportValidTill.mandatory) &&
                ['1', '2'].includes(bookConfig.passportValidTill.display),
            },
            email: {
              required:
                ['1', '2'].includes(bookConfig.email.mandatory) &&
                ['1', '2'].includes(bookConfig.email.display),
            },
            phone: {
              required:
                ['1', '2'].includes(bookConfig.phone.mandatory) &&
                ['1', '2'].includes(bookConfig.phone.display),
            },
          }
          : {
            ...shortPassengerValidation,
            passportNumber: {
              required:
                bookConfig.passportNumber.mandatory === '1' &&
                bookConfig.passportNumber.display === '1',
            },
            passportValidTill: {
              required:
                bookConfig.passportValidTill.mandatory === '1' &&
                bookConfig.passportValidTill.display === '1',
            },
            email: {
              required: bookConfig.email.mandatory === '1' && bookConfig.email.display === '1',
            },
            phone: {
              required: bookConfig.phone.mandatory === '1' && bookConfig.phone.display === '1',
            },
          };

      console.log('validation', validation, passenger);

      if (!validate(validation, passenger)) {
        result = false;
      }
    });

    if (bookConfig.docket.mandatory === '1' && !docket) {
      result = false;
      console.log('docket', docket);
    }

    if (bookConfig.tourSubject.mandatory === '1' && !tourSubject) {
      result = false;
      console.log('tourSubject', tourSubject);
    }

    const isValid =
      result &&
      termsAccepted &&
      (book.roomClass.hotelRooms?.[0]?.typ !== 'apartment' || props.isApartmentAccepted) &&
      (!cxlExpired || cxlAccepted) &&
      (!book.cancellationPolicy.isPriceChange || priceChangeAccepted || book.cancellationPolicy?.newPrice <= book.cancellationPolicy?.oldPrice);

    if (!isValid) {
      console.log('type' + book.roomClass.hotelRooms?.[0]?.typ !== 'apartment');
    }

    return isValid;
  };

  const getPassengerNumber = (passengerIndex: number, roomIndex: number) => {
    const previosRoomPassengersCount = book.roomClass.hotelRooms.reduce((acc, room, i) => {
      if (i < roomIndex) {
        return acc + room.paxCodes.length;
      }
      return acc;
    }, 0);

    return previosRoomPassengersCount + passengerIndex + 1;
  };

  const needToDisplay = (val: S3FieldOptions, pasIndex: number, roomIndex: number) => {
    return val.display === '1' || (val.display === '2' && pasIndex === 0 && roomIndex === 0);
  };

  const isRequired = (val: S3FieldOptions, pasIndex: number, roomIndex: number) => {
    return (
      val.mandatory === '1' ||
      (val.display === '2' && val.mandatory === '2' && pasIndex === 0 && roomIndex === 0)
    );
  };

  const onClickPasteName = () => {
    const paxesStr = sessionStorage.getItem('activeMyOrderNames');
    if (paxesStr) {
      const paxes = JSON.parse(paxesStr);

      let newModel = { ...form };
      for (let i = 0; i < paxes.length && i < form.passengers.length; i += 1) {
        if (paxes[i].paxTypeCode === form.passengers[i].paxTypeCode) {
          const { phone1, phone2 } = paxes[i];

          console.log('paxes', paxes[i], form.passengers[i]);
          newModel.passengers[i] = {
            ...form.passengers[i],
            firstName: paxes[i].firstName,
            lastName: paxes[i].lastName,
            phone2: phone2.replace('972', ''),
            phone: phone1 ? phone1 : form.passengers[i].phone,
            email: paxes[i].email1,
            gender: paxes[i].gender,
            title: paxes[i].paxTitle
              ? paxes[i].paxTitle.toLowerCase().replace('.', '')
              : form.passengers[i].title,
            dob: new Date(paxes[i].dob),
            passportNumber: paxes[i].passportNumber,
            passportValidTill: new Date(paxes[i].passportValidTill),
          };
        }
      }

      setForm(newModel);
    }
  };

  return (
    <div className="details book">
      <div className="default-shadow book-form">
        <div className="mb-5">
          <span className="title-underline text-lg flex-between title-header-grey">
            {t('passenger.details')}
            {showIsPasteName && (
              <Button
                title="Paste Names"
                icon={faPaste}
                iconclassname="mr-5"
                className="btn btn-default text-nowrap btn-sm"
                onClick={onClickPasteName}
              />
            )}
          </span>
        </div>
        <div className="color-danger mb-10 text-xsm">{t('only_english_input')}</div>
        <div>
          {book.roomClass.hotelRooms.map((room, roomIindex) => (
            <div className="book-form-room" key={roomIindex}>
              <div className="mb-30">
                <div className="text-md mb-10">
                  <FontAwesomeIcon icon={faHotel} /> {t('passenger.room')}{' '}
                  {!!book.roomClass.hotelRooms.length && roomIindex + 1}
                </div>

                <div
                  className="col-12 text-bold text-black text-md"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                >
                  <span>
                    {room.desc} {book.roomClass.board.basis.desc},{' '}
                  </span>
                  <span>
                    {room.numAdt} {t('search.adults')}
                    {!!room.numCnn && `, ${room.numCnn} ${t('search.children')}`}
                  </span>
                </div>
              </div>
              {form.passengers
                .filter((passenger) => room.paxCodes.includes(passenger.paxCode))
                .map((passenger, passengerIndex, array) => (
                  <div className="book-form-block mb-0" key={passengerIndex}>
                    {passenger.paxTypeCode === PaxHotelType.ADULT && (
                      <div className="title text-md">
                        <FontAwesomeIcon icon={faPerson} /> {t('passenger.guest')}{' '}
                        {getPassengerNumber(passengerIndex, roomIindex)}
                      </div>
                    )}
                    {passenger.paxTypeCode === PaxHotelType.CHILD && (
                      <div className="title text-md">
                        <FontAwesomeIcon icon={faChild} />{' '}
                        {passenger.age && passenger.age === 1 && <span>{t('search.infant')}</span>}
                        {passenger.age && passenger.age > 1 && (
                          <span>{`${t('child')} (${passenger.age} ${t('passenger.years')})`} </span>
                        )}
                      </div>
                    )}

                    <div className="form-wrapper">
                      {passenger.paxTypeCode === PaxHotelType.ADULT && (
                        <Fragment>
                          {b2b ? (
                            <FormGroupSelect
                              className="col-md-2 col-12"
                              value={passenger.title}
                              label={t('passenger.title')}
                              onChange={(title) => onChange({ ...passenger, title })}
                              isSubmitted={isSubmitted}
                              validation={passengerValidation.title}
                              options={titlePassengerOptions}
                              needToTranslate={true}
                            />
                          ) : (
                            <FormGroupSelect
                              className="col-md-2 col-12"
                              value={passenger.gender}
                              label={t('passenger.gender')}
                              onChange={(gender) => onChange({ ...passenger, gender })}
                              isSubmitted={isSubmitted}
                              validation={passengerValidation.gender}
                              options={sexOptions}
                              needToTranslate={true}
                            />
                          )}
                        </Fragment>
                      )}
                      {passenger.paxTypeCode === PaxHotelType.CHILD && (
                        <Fragment>
                          <FormGroupDatepicker
                            className="col-md-5 col-12"
                            value={passenger.dob!}
                            label={t('passenger.dob')}
                            onChange={(dob) => onChange({ ...passenger, dob })}
                            minDate={addYears(
                              -(passenger.age || 0) - 1,
                              new Date(book.roomClass.checkIn)
                            )}
                            maxDate={addYears(
                              -(passenger.age || 0),
                              new Date(book.roomClass.checkIn)
                            )}
                          />
                        </Fragment>
                      )}
                      <FormGroup
                        name="firstName"
                        className="col-md-5 col-12"
                        value={passenger.firstName}
                        label={t('passenger.fn')}
                        onChange={(firstName) => onChange({ ...passenger, firstName })}
                        isSubmitted={isSubmitted}
                        validation={
                          passengerIndex === 0
                            ? passengerValidation.firstName
                            : shortPassengerValidation.firstName
                        }
                      />
                      <FormGroup
                        name="lastName"
                        className="col-md-5 col-12"
                        value={passenger.lastName}
                        label={t('passenger.ln')}
                        onChange={(lastName) => onChange({ ...passenger, lastName })}
                        isSubmitted={isSubmitted}
                        validation={
                          passengerIndex === 0
                            ? passengerValidation.lastName
                            : shortPassengerValidation.lastName
                        }
                      />

                      {needToDisplay(bookConfig.phone, passengerIndex, roomIindex) && (
                        <PhoneInput
                          onChange={(phone) => onChange({ ...passenger, phone })}
                          value={passenger.phone}
                          isSubmitted={isSubmitted}
                          className="col-md-6 col-12"
                          validation={{
                            ...passengerValidation.phone,
                            required: isRequired(bookConfig.phone, passengerIndex, roomIindex),
                          }}
                          initialValue={passenger.phone || user?.phone || passenger.phone2}
                        />
                      )}

                      {needToDisplay(bookConfig.email, passengerIndex, roomIindex) && (
                        <FormGroup
                          name="email"
                          className="col-md-6 col-12"
                          value={passenger.email}
                          label={t('passenger.email')}
                          onChange={(email) => onChange({ ...passenger, email })}
                          isSubmitted={isSubmitted}
                          validation={{
                            ...passengerValidation.email,
                            required: isRequired(bookConfig.email, passengerIndex, roomIindex),
                          }}
                        />
                      )}
                      {needToDisplay(bookConfig.passportNumber, passengerIndex, roomIindex) && (
                        <FormGroup
                          onChange={(passportNumber) => onChange({ ...passenger, passportNumber })}
                          value={passenger.passportNumber}
                          label={t('passenger.passport')}
                          className="col-md-4 col-12"
                          validation={{
                            required: isRequired(
                              bookConfig.passportNumber,
                              passengerIndex,
                              roomIindex
                            ),
                          }}
                          isSubmitted={isSubmitted}
                        />
                      )}
                      {needToDisplay(bookConfig.passportValidTill, passengerIndex, roomIindex) && (
                        <FormGroupDatepicker
                          onChange={(passportValidTill) =>
                            onChange({ ...passenger, passportValidTill })
                          }
                          value={passenger.passportValidTill}
                          label={t('passenger.passport_valid_till')}
                          className="col-md-4 col-12"
                          validation={{
                            required: isRequired(
                              bookConfig.passportValidTill,
                              passengerIndex,
                              roomIindex
                            ),
                          }}
                          isSubmitted={isSubmitted}
                          maxDate={addYears(10)}
                          minDate={addYears(0)}
                        />
                      )}
                    </div>
                    {passengerIndex < array.length - 1 && (
                      <div className="divider mb-20 mt-10 mh-15"></div>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </div>

          <OrderTripCompany
            companyId={props.companyId}
            tripId={props.tripId}
            onChangeCompanyId={props.onChangeCompanyId}
            onChangeTripId={props.onChangeTripId}
          />

        <div className="mb-20"></div>
        <div className="ml-15">
          <div className="checkbox-inline">
            <Checkbox
              checked={termsAccepted}
              onChange={setTermsAccepted}
              label={''}
              className="mb-20"
              hasError={isSubmitted && !termsAccepted}
              blicked={true}
              htmlLabel={
                <Fragment>
                  <span className="mr-10">{t('passenger.confirm')}</span>
                  <span
                    className="btn-lnk"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsConditionsOpen(true);
                    }}
                  >
                    {t('passenger.read_more')}
                  </span>
                </Fragment>
              }
            />
          </div>
          {book.cancellationPolicy.isPriceChange && (book.cancellationPolicy?.newPrice > book.cancellationPolicy?.oldPrice) && (
            <div className="checkbox-inline text-lh">
              <span className="text-bold">
                <Checkbox
                  checked={priceChangeAccepted}
                  onChange={setPriceChangeAccepted}
                  label={'Please confirm that you accept the updated price'}
                  className="checkbox-danger color-danger mb-20"
                />
              </span>

              <span
                className="text-light cur-poi position-relative"
                style={{ top: 2 }}
                onClick={() => setPriceChangeAccepted(!priceChangeAccepted)}
              >
                <span className="text-bold text-lg color-danger">
                  {formatPrice(
                    Math.round(book.cancellationPolicy.newPrice),
                    book.cancellationPolicy.currency
                  )}{' '}
                </span>
                (Original Price:{' '}
                {formatPrice(
                  Math.round(book.cancellationPolicy.oldPrice),
                  book.cancellationPolicy.currency
                )}
                )
              </span>
            </div>
          )}
          {isConditionsOpen && (
            <GeneralConditionsModal product="Hotels" onClose={() => setIsConditionsOpen(false)} />
          )}
          {cxlExpired && (
            <Checkbox
              checked={cxlAccepted}
              onChange={setCxlAccepted}
              label={t('passenger.attention')}
              className="checkbox-danger color-danger mb-20"
            />
          )}

          <div className="show-tablet w100p mb-10"></div>

          <div className="title-footer-grey">
            <Button className="btn btn-primary btn-lg" title={t('continue')} onClick={confirm} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(BookForm, 'BookForm');
